import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { dbService } from "../services/dbService";
import homeBg from "../assets/images/home.jpeg";

export default function Profile() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("global");
  const { currentUser, logout } = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedLanguage = localStorage.getItem("language");
    if (storedLanguage) {
      i18n.changeLanguage(storedLanguage);
    }
  }, [i18n]);

  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser) {
        try {
          const data = await dbService.getUserData(currentUser.uid);
          setUserData(data);
        } catch (error) {
          console.error("Error fetching user data:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchUserData();
  }, [currentUser]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen text-white">
        Loading...
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="flex justify-center items-center h-screen text-white">
        No user data found.
      </div>
    );
  }

  const personalFields = [
    { name: "First_Name.text", value: userData.firstName },
    { name: "Last_Name.text", value: userData.lastName },
    { name: "Address.text", value: userData.address },
    { name: "City.text", value: userData.city },
    { name: "State.text", value: userData.state },
    { name: "Country.text", value: userData.country },
    { name: "Nationality.text", value: userData.nationality },
    { name: "Email.text", value: userData.email },
    { name: "Phone.text", value: userData.phoneNumber },
    { name: "Mobile.text", value: userData.mobile },
  ];

  const membershipFields = [
    { name: "Coowner Name.text", value: userData.coowner },
    { name: "Tourism Development.text", value: userData.tourism_development },
    { name: "Membership.text", value: userData.membership },
    { name: "Flyxom_Membership.text", value: userData.flyxom_membership },
    { name: "Purchase Date.text", value: userData.date },
    { name: "City Of Purchase.text", value: userData.membercity },
    { name: "State Of Purchase.text", value: userData.memberstate },
    { name: "Country Of Purchase.text", value: userData.membercountry },
  ];

  const renderField = (field, index) => (
    <li
      className="flex justify-between py-3 border-b border-gray-600 last:border-b-0"
      key={index}
    >
      <span className="font-semibold text-[#fcab21]">{t(field.name)}:</span>
      <span className="text-white text-right">{field.value}</span>
    </li>
  );

  return (
    <div
      className="min-h-screen w-full bg-center bg-cover bg-no-repeat"
      style={{ backgroundImage: `url(${homeBg})` }}
    >
      <div className="w-full h-32 md:h-40 lg:h-48 bg-gradient-to-b from-gray-900 to-gray-700 border-b-4 border-[#fcab21]" />

      <div className="flex flex-col items-center -mt-16 md:-mt-20 px-4">
        <img
          src={new URL("../assets/images/user.jpg", import.meta.url).href}
          className="w-32 h-32 md:w-40 md:h-40 border-4 border-white rounded-full object-cover"
          alt="User Profile"
        />
        <div className="flex items-center space-x-2 mt-4">
          <p className="text-xl md:text-2xl text-white font-semibold">
            {userData.firstName}
          </p>
          <span className="bg-blue-500 rounded-full p-1" title="Verified">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="text-gray-100 h-2.5 w-2.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="4"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </span>
        </div>
        <p className="text-lg md:text-xl text-white mt-1">
          {userData.lastName}
        </p>
        <p className="text-sm text-slate-200 mt-2">{userData.address}</p>
      </div>

      <div className="flex justify-center px-4 md:px-8 mt-6 lg:mt-8">
        <div className="flex flex-col sm:flex-row items-center gap-3 md:gap-4 w-full max-w-md">
          <button
            onClick={() => navigate("/claim", { replace: true })}
            className="w-full sm:flex-1 flex items-center justify-center shadow-xl bg-[#fcab21] hover:bg-yellow-600 text-white font-semibold px-6 py-3 rounded-lg text-sm md:text-base transition-all duration-200"
          >
            <span>{t("Claim.text")}</span>
          </button>
          <button
            onClick={handleLogout}
            className="w-full sm:flex-1 flex items-center justify-center shadow-xl bg-[#fcab21] hover:bg-yellow-600 text-white font-semibold px-6 py-3 rounded-lg text-sm md:text-base transition-all duration-200"
          >
            <img
              className="w-5 h-5 mr-2"
              src={new URL("../assets/images/logout.svg", import.meta.url).href}
              alt="Logout Icon"
            />
            <span>{t("Logout.text")}</span>
          </button>
        </div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 xl:px-16 2xl:px-24 mt-8 pb-12 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          <div className="w-full">
            <div className="rounded-lg shadow-2xl p-6 md:p-8 bg-gray-800/65 bg-blend-multiply backdrop-blur-md border border-gray-700/50">
              <h4 className="text-2xl md:text-3xl text-[#fcab21] font-bold mb-6 pb-3 border-b-2 border-[#fcab21]/30">
                {t("Personal.text")}
              </h4>
              <ul className="text-white">{personalFields.map(renderField)}</ul>
            </div>
          </div>

          <div className="w-full">
            <div className="rounded-lg shadow-2xl p-6 md:p-8 bg-gray-800/65 bg-blend-multiply backdrop-blur-md border border-gray-700/50">
              <h4 className="text-2xl md:text-3xl text-[#fcab21] font-bold mb-6 pb-3 border-b-2 border-[#fcab21]/30">
                {t("Booking.text")}
              </h4>
              <ul className="text-white">
                {membershipFields.map(renderField)}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
